import { LinkProps, RegisteredRouter } from '@tanstack/react-router';
import styled from 'styled-components';

import * as icons from '@creditornot/alchemy-react/icons';
import { Tag } from '@creditornot/cb-tag';
import { useFormatMessage } from 'common/hooks/useFormatMessage';

import { CopyableButton } from '../CopyableButton';
import { LineClamp } from '../LineClamp';
import { Stack } from '../Stack';
import { TextLink } from '../TextLink';
import { Thumbnail } from '../Thumbnail';

type KviType = 'KVI' | 'NON_KVI' | 'SUPER_KVI';

type Props = {
  to?: LinkProps<RegisteredRouter>['to'];
  params?: LinkProps<RegisteredRouter>['params'];
  imgSrc?: string;
  name?: string;
  gtin?: string | null;
  productCode?: string | null;
  vendorSku?: string | null;
  kviType?: KviType;
};

const GtinButtonContainer = styled.div`
  min-width: calc(15ch + 20px); /* GTIN-14 with padding */
`;

const ProductCodeContainer = styled.div`
  min-width: calc(12ch + 20px); /* 12 symbols with padding */
`;

const ProductCodeTextLink = styled(TextLink)`
  font-weight: 500;
  white-space: break-spaces;
`;

const CodesContainer = styled(Stack)`
  width: 100%;
  flex-wrap: wrap;
`;

export function ProductCard(props: Props) {
  const format = useFormatMessage();
  const { to, params, imgSrc, name, gtin, productCode, vendorSku, kviType } = props;

  const nameToDisplay = name ?? format('common.not_available_acronym');

  //Check if imgSrc prop is undefined because no prop passed, or because the value of it is undefined
  //to display image placeholder when it's passed but value undefined
  const isImgSrcPropPassed = Object.prototype.hasOwnProperty.call(props, 'imgSrc');
  return (
    <Stack direction="row" gap="regularMedium" alignItems="center">
      {isImgSrcPropPassed && <Thumbnail width={40} height={40} imgSrc={imgSrc} altText={name} />}
      <Stack direction="column" gap="narrowSmall">
        <LineClamp lines={4} title={nameToDisplay} data-test-id="product-card-name">
          {to ? (
            <ProductCodeTextLink to={to} params={params}>
              {nameToDisplay}
            </ProductCodeTextLink>
          ) : (
            <span>{nameToDisplay}</span>
          )}
        </LineClamp>
        <CodesContainer
          gap="regularMedium"
          direction="row"
          alignItems="center"
          justifyContent="flex-start"
        >
          <GtinButtonContainer>
            <CopyableButton
              variant="text"
              text={format('common.product_card.click_to_copy_gtin')}
              icon={icons.barcode}
              testId="product-card-gtin"
              ariaLabel={`${format('common.product_card.click_to_copy_gtin')} ${gtin}`}
            >
              {gtin || '–'}
            </CopyableButton>
          </GtinButtonContainer>
          <ProductCodeContainer>
            <CopyableButton
              variant="text"
              text={format('common.product_card.click_to_copy_product_code')}
              icon={icons.tag}
              testId="product-card-product-code"
              ariaLabel={`${format('common.product_card.click_to_copy_product_code')} ${productCode}`}
            >
              {productCode || '–'}
            </CopyableButton>
          </ProductCodeContainer>
          {vendorSku && (
            <CopyableButton
              variant="text"
              text={format('common.product_card.click_to_copy_sku')}
              icon={icons.market}
              testId="product-card-vendor-sku"
              ariaLabel={`${format('common.product_card.click_to_copy_sku')} ${vendorSku}`}
            >
              {vendorSku}
            </CopyableButton>
          )}
          {kviType && ['KVI', 'SUPER_KVI'].includes(kviType) && (
            <Tag variant="secondaryNeutral">{format(`purchase_orders.kvi:${kviType}`)}</Tag>
          )}
        </CodesContainer>
      </Stack>
    </Stack>
  );
}
